import { NetworkStatus } from '@apollo/client';
import { useCallback } from 'react';

import { getNextPage, mergePaginatedResult } from '@/adapters/graphql/pagination';

interface PaginationEntity {
  pagination?: { currentPage: number; totalPages: number };
}

interface UsePaginatedFetchMoreOptions<TQuery extends Record<string, unknown>> {
  readonly networkStatus: NetworkStatus;
  readonly data: TQuery | undefined;
  readonly entityKey: keyof TQuery & string;
  readonly fetchMore: (options: {
    variables: Record<string, unknown>;
    updateQuery: (prev: TQuery, opts: { fetchMoreResult?: TQuery }) => TQuery;
  }) => unknown;
  readonly buildVariables: (nextPage: number) => Record<string, unknown>;
}

interface UsePaginatedFetchMoreResult {
  readonly isFetchingMore: boolean;
  readonly handleEndReached: () => void;
}

export const usePaginatedFetchMore = <TQuery extends Record<string, unknown>>({
  networkStatus,
  data,
  entityKey,
  fetchMore,
  buildVariables,
}: UsePaginatedFetchMoreOptions<TQuery>): UsePaginatedFetchMoreResult => {
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  const handleEndReached = useCallback(() => {
    const entity = data?.[entityKey] as PaginationEntity | undefined;
    if (isFetchingMore || !entity?.pagination) {
      return;
    }

    const nextPage = getNextPage(entity.pagination);
    if (!nextPage) {
      return;
    }

    void fetchMore({
      variables: buildVariables(nextPage),
      updateQuery: (previous: TQuery, { fetchMoreResult }: { fetchMoreResult?: TQuery }) => {
        if (!fetchMoreResult) {
          return previous;
        }

        return mergePaginatedResult(previous, fetchMoreResult, entityKey);
      },
    });
  }, [buildVariables, data, entityKey, fetchMore, isFetchingMore]);

  return { isFetchingMore, handleEndReached };
};
